import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { AffiliatesService, type AffiliateRecord } from '@/services/affiliates'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { TableSkeleton } from '@/components/skeletons'
import { Link2, RefreshCw, AlertCircle, Search, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useDebounce } from '@/hooks/useDebounce'
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@/components/ui/pagination'
import { generatePaginationPages } from '@/lib/pagination'

const COLUMN_COUNT = 5
const PAGE_SIZE = 20

type ReferredSort = 'referred_desc' | 'referred_asc' | null

export default function AffiliatesList() {
    const navigate = useNavigate()
    const [affiliates, setAffiliates] = useState<AffiliateRecord[]>([])
    const [page, setPage] = useState(1)
    const [total, setTotal] = useState<number | null>(null)
    const [totalPages, setTotalPages] = useState(1)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchQuery, setSearchQuery] = useState('')
    const debouncedSearch = useDebounce(searchQuery, 300)
    const [referredSort, setReferredSort] = useState<ReferredSort>(null)

    const fetchAffiliates = useCallback(async () => {
        try {
            setIsLoading(true)
            setError(null)
            const data = await AffiliatesService.getAll(page, PAGE_SIZE, debouncedSearch, referredSort ?? undefined)
            setAffiliates(data.affiliates)
            setTotal(data.total)
            setTotalPages(data.totalPages)
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load affiliates. Please try again later.')
        } finally {
            setIsLoading(false)
        }
    }, [page, debouncedSearch, referredSort])

    // A search or sort change goes back to page 1 before it fetches, so it
    // costs one request rather than one for the old page and one for the first.
    const filterKey = JSON.stringify([debouncedSearch, referredSort])
    const lastFilterKey = useRef(filterKey)
    useEffect(() => {
        if (lastFilterKey.current !== filterKey) {
            lastFilterKey.current = filterKey
            if (page !== 1) {
                setPage(1)
                return
            }
        }
        fetchAffiliates()
    }, [fetchAffiliates, filterKey, page])

    // Cycle: none -> descending -> ascending -> none
    const cycleReferredSort = () => {
        setReferredSort(prev =>
            prev === null ? 'referred_desc' : prev === 'referred_desc' ? 'referred_asc' : null
        )
    }

    const SortIcon = referredSort === 'referred_desc'
        ? ChevronDown
        : referredSort === 'referred_asc'
            ? ChevronUp
            : ChevronsUpDown

    return (
        <div className="space-y-4 sm:space-y-6 w-full px-4 sm:px-0">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
                    Affiliates{' '}
                    {total !== null && total > 0 && (
                        <span className="text-muted-foreground font-normal">
                            ({total})
                        </span>
                    )}
                </h1>
                <Button
                    variant="outline"
                    onClick={() => fetchAffiliates()}
                    disabled={isLoading}
                    className="w-full sm:w-auto"
                >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {error ? (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            ) : (
                <div className="rounded-lg border bg-card">
                    <div className="p-4 border-b">
                        <div className="relative w-full sm:max-w-sm">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="Search affiliates by name or email..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/50">
                                    <TableHead className="min-w-[200px]">Affiliate</TableHead>
                                    <TableHead className="text-center w-[120px]">
                                        <button
                                            type="button"
                                            onClick={cycleReferredSort}
                                            className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-foreground"
                                        >
                                            Referred
                                            <SortIcon className={`h-3.5 w-3.5 ${referredSort ? '' : 'text-muted-foreground'}`} />
                                        </button>
                                    </TableHead>
                                    <TableHead className="text-center w-[120px]">Converted</TableHead>
                                    <TableHead className="text-center w-[140px]">MRR Contribution</TableHead>
                                    <TableHead className="text-center w-[120px]">Conv. Rate</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody className={isLoading ? 'pointer-events-none' : ''}>
                                {isLoading ? (
                                    <TableSkeleton columns={COLUMN_COUNT} rows={5} />
                                ) : affiliates.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={COLUMN_COUNT} className="h-48 text-center text-muted-foreground">
                                            <div className="flex flex-col items-center justify-center">
                                                <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                                                    <Link2 className="h-6 w-6 text-muted-foreground" />
                                                </div>
                                                <h3 className="text-lg font-medium mb-2">No affiliates</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    {debouncedSearch
                                                        ? 'No affiliates match your search. Try a different query.'
                                                        : 'No affiliates have joined the program yet.'}
                                                </p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    affiliates.map((aff) => (
                                        <TableRow
                                            key={aff.id}
                                            className="cursor-pointer hover:bg-muted/50 transition-colors"
                                            onClick={() => navigate(`/affiliates/${aff.id}`)}
                                        >
                                            <TableCell className="font-medium">
                                                <div className="flex flex-col min-w-0">
                                                    <span className="truncate max-w-[200px]">{aff.name}</span>
                                                    {aff.email && (
                                                        <span className="text-xs text-muted-foreground truncate max-w-[200px]">{aff.email}</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-center font-medium text-sm">{aff.referredCount}</TableCell>
                                            <TableCell className="text-center font-medium text-sm">{aff.convertedCount}</TableCell>
                                            <TableCell className="text-center font-medium text-sm">
                                                ${aff.mrrContribution.toLocaleString()}/mo
                                            </TableCell>
                                            <TableCell className="text-center font-medium text-sm">
                                                {aff.conversionRate}%
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-4 border-t">
                            <p className="text-sm text-muted-foreground">
                                Showing {affiliates.length} of {total !== null ? total : '...'} affiliates
                            </p>
                            <Pagination className={`mx-0 w-auto ${isLoading ? 'pointer-events-none opacity-50' : ''}`}>
                                <PaginationContent>
                                    <PaginationItem>
                                        <PaginationPrevious
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                        />
                                    </PaginationItem>
                                    {generatePaginationPages(page, totalPages).map((item, i) =>
                                        item === 'ellipsis' ? (
                                            <PaginationItem key={`ellipsis-${i}`}>
                                                <span className="px-2 text-muted-foreground">…</span>
                                            </PaginationItem>
                                        ) : (
                                            <PaginationItem key={item}>
                                                <PaginationLink
                                                    onClick={() => setPage(item as number)}
                                                    isActive={(item as number) === page}
                                                    className="cursor-pointer"
                                                >
                                                    {item}
                                                </PaginationLink>
                                            </PaginationItem>
                                        )
                                    )}
                                    <PaginationItem>
                                        <PaginationNext
                                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                            className={page === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                        />
                                    </PaginationItem>
                                </PaginationContent>
                            </Pagination>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
